/**
 * main.ts — the application shell (P1 US-118; P2 wave 4 added accounts, projects and cases).
 *
 * This is the only file that knows about all the others: every module in `src/` is a pure function
 * or a narrow controller, testable without a DOM, and the wiring lives here so that the Playwright
 * suite is the only thing that has to cover it.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * NOTHING HERE IMPORTS `@ronceray/engine` — AND THAT IS THE PRODUCT, NOT A LINT RULE
 * ────────────────────────────────────────────────────────────────────────────────────────────────
 * The engine is the commercial asset. It stays on the server, and this file is where a careless
 * `import` would most plausibly appear, because it is the one place that would find it convenient.
 * Three independent controls stop it: `no-restricted-imports` in the root ESLint config, the CI
 * canary scan of the built bundle including source maps, and `test/bundle.test.ts`, which greps the
 * built output for engine-only identifiers in case somebody retypes a formula rather than importing
 * one.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE SHELL IS NOT EDITABLE, SO P2'S CHROME IS BUILT AT RUNTIME
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `index.html`'s body is oracle lines 126–201, byte for byte, and `test/shell.test.ts` asserts it.
 * That is the fidelity guarantee the whole port rests on, so P2 adds no markup to it: the identity
 * strip, the project controls, the overlay, the version banner and the diff panel are all created
 * here and appended. `src/p2.css` styles them, and it is a second file for the same reason —
 * `styles.css` is the oracle's own CSS, compared byte for byte.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * BOOT ORDER
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 *  1. Build all three forms and wire the oracle's chrome. Cheap, synchronous, and needs no session:
 *     `ecrire` on a form that has not been built is a silent no-op, so the panes must exist before
 *     anything can restore a case into them.
 *  2. Build P2's chrome and hide it.
 *  3. **Ask who we are.** `GET /v1/me` is the first request, and a 401 is an answer rather than an
 *     error: it means "sign in", with the destination remembered. NOTHING calculates before this —
 *     a calculator that boots, fires a request and paints "erreur du serveur" is how wave 2's
 *     session requirement would look to a user.
 *  4. Signed in: reveal the chrome, learn the running versions, and honour the route.
 *
 * Step 3 is the change that makes the beta work at all after wave 2. See `api-client.ts` on
 * `credentials: 'include'`, which is the other half of it.
 */

import './p2.css';

import type { CaseResponse, MeResponse, VersionStamp } from '@ronceray/contract';

import { calcClient, failureMessage, SUPERSEDED } from './api-client';
import * as store from './api-store';
import { authView, identityHTML } from './auth-ui';
import { caseHeaderHTML, caseView, saveControlHTML } from './case-ui';
import { must } from './dom';
import { construire, ecrire, lire } from './form';
import type { FicheModule } from './fiches';
import { imprimer, type LastResult } from './note';
import { optimisePanel } from './optimise-ui';
import { projectsView } from './projects-ui';
import { recalcView, versionBannerHTML } from './recalc-ui';
import { messageHTML, panelHTML } from './render';
import { buildSavedFile, downloadSavedFile, openSavedFile, tabToRestore } from './saved-file-ui';
import { router, type Route } from './routes';
import { isModule, MODULES, tabs } from './tabs';

/**
 * The last successful result per module — the oracle's `DERNIER` (line 1051).
 *
 * The note renderer reads it rather than recalculating, which is what guarantees the printed
 * document matches what was on screen when the user pressed print. **A stored case writes into it
 * too** (US-214), so a note printed from a case opened out of the database is the stored snapshot
 * rather than a fresh calculation — which is D10 reaching all the way to the paper.
 *
 * Cleared for a module only by a newer successful result, never by a failure: a transient network
 * error must not silently make a previously printable case unprintable.
 */
const DERNIER = new Map<FicheModule, LastResult>();

const client = calcClient();

const projetInput = must<HTMLInputElement>('#projet');

/* ================================================================================================
 * P2 chrome, created here because the shell may not be edited
 * ============================================================================================== */

const header = must('header');
const nav = must('nav');
const mainEl = must('main');

const chrome = document.createElement('div');
chrome.className = 'p2-chrome';
chrome.hidden = true;
header.append(chrome);

/** The version banner and the open-case strip, between the tabs and the panes. */
const banner = document.createElement('div');
banner.id = 'p2-banner';
nav.after(banner);

const openBar = document.createElement('div');
openBar.id = 'p2-openbar-host';
banner.after(openBar);

/** The recalculation diff. Hidden until asked for; never shown beside a stale snapshot. */
const diffHost = document.createElement('div');
diffHost.id = 'p2-diffhost';
diffHost.hidden = true;
openBar.after(diffHost);

/**
 * The overlay. It **replaces** the calculator rather than floating over it — a half-visible form
 * behind a modal invites typing into inputs that are not the thing on screen.
 */
const overlay = document.createElement('div');
overlay.id = 'p2-overlay';
overlay.className = 'p2-overlay';
overlay.hidden = true;
mainEl.before(overlay);

/**
 * Show the overlay, or the calculator. Never both.
 *
 * The overlay **replaces** the calculator rather than floating over it, and that means actually
 * hiding the rest: a form left visible behind a sign-in screen invites typing into inputs that are
 * not the thing on screen, and tabs left clickable behind a project browser switch a pane nobody
 * can see. Checked in a real browser, where the first version of this function — which hid nothing
 * — was obviously wrong at a glance and invisible to every unit test.
 */
function setOverlay(visible: boolean): void {
  overlay.hidden = !visible;
  mainEl.hidden = visible;
  nav.hidden = visible;
  banner.hidden = visible;
  openBar.hidden = visible;
  if (visible) diffHost.hidden = true;
}

/* ================================================================================================
 * State
 * ============================================================================================== */

let me: MeResponse | undefined;
/** What the API is running now. Read once, after sign-in; the banner compares against it. */
let running: VersionStamp | undefined;
let currentProject: { id: string; name: string } | undefined;
/** The stored case on screen, if any. Cleared as soon as the user edits a field. */
let openedCase: CaseResponse | undefined;

/* ================================================================================================
 * Calculation — unchanged from P1, plus one branch
 * ============================================================================================== */

/** Recalculate one module and render whatever comes back. */
async function calculer(mod: FicheModule): Promise<void> {
  const input = lire(mod);
  const box = must(`#res_${mod}`);

  const result = await client.calculate(mod, input);

  // A newer edit superseded this one. Leave the panel alone: the newer request will paint it, and
  // touching it here is exactly how a stale answer reaches the screen.
  if (result === SUPERSEDED) return;

  if (!result.ok) {
    // A session that expired mid-session is not a calculation failure. Sending the user to sign in
    // — with this route remembered — is the only response that leads anywhere.
    if (result.kind === 'auth') {
      requireSignIn();
      return;
    }
    box.innerHTML = messageHTML(failureMessage(result.kind));
    return;
  }

  DERNIER.set(mod, { input, envelope: result.value });
  box.innerHTML = panelHTML(mod, input, result.value);
}

/**
 * A recalculation the user asked for by editing a field.
 *
 * It closes the stored-case view, because the case on screen is no longer that case: the inputs
 * have changed. Leaving the strip up would put a saved case's name and date above numbers it never
 * produced.
 */
function calculerAfterEdit(mod: FicheModule): void {
  if (openedCase !== undefined) closeStoredCase();
  void calculer(mod);
}

const tabController = tabs('lamine', (mod) => {
  // A module revealed for the first time calculates now rather than at boot.
  void calculer(mod);
});

/* ================================================================================================
 * 1. Forms and the oracle's own chrome
 * ============================================================================================== */

for (const mod of MODULES) {
  construire(mod, (changed) => {
    calculerAfterEdit(changed);
  });
}

for (const tab of document.querySelectorAll<HTMLButtonElement>('.tab')) {
  tab.addEventListener('click', () => {
    const mod = tab.dataset['mod'];
    if (isModule(mod)) tabController.onglet(mod);
  });
}

const panels = new Map(
  MODULES.map((mod) => [
    mod,
    optimisePanel(mod, {
      readInput: (m) => lire(m),
      onApply: (m, patch) => {
        ecrire(m, patch);
        calculerAfterEdit(m);
      },
    }),
  ]),
);

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-opt]')) {
  button.addEventListener('click', () => {
    const mod = button.dataset['opt'];
    if (isModule(mod)) void panels.get(mod)?.run();
  });
}

for (const button of document.querySelectorAll<HTMLButtonElement>('[data-print]')) {
  button.addEventListener('click', () => {
    const mod = button.dataset['print'];
    if (isModule(mod)) imprimer(mod, DERNIER.get(mod), projetInput.value);
  });
}

/*
 * LOCAL SAVE AND OPEN, UNCHANGED (US-214's second criterion).
 *
 * `#save` still downloads a `.json` and `#open` still reads one. They are how a user gets their
 * data OUT of the product, and during a pilot that reassurance is worth more than a tidy button
 * count — so the server save is a separate, injected control rather than a replacement.
 */
must('#save').addEventListener('click', () => {
  const courant = tabController.courant;
  const data = buildSavedFile(projetInput.value, courant, (mod) => lire(mod));
  downloadSavedFile(data, lire(courant)['repere']);
});

const fileInput = must<HTMLInputElement>('#file');

must('#open').addEventListener('click', () => fileInput.click());

fileInput.addEventListener('change', () => {
  const file = fileInput.files?.[0];
  // Reset unconditionally, and BEFORE the async read. Without it, choosing the same file twice in
  // a row fires no `change` event the second time and the tool looks broken for a reason nobody
  // can see. The oracle does the same at line 1245.
  fileInput.value = '';
  if (file === undefined) return;

  void file.text().then((text) => {
    const opened = openSavedFile(text);

    if (!opened.ok) {
      must(`#res_${tabController.courant}`).innerHTML = messageHTML(opened.message);
      return;
    }

    closeStoredCase();
    projetInput.value = opened.file.projet;

    for (const mod of MODULES) {
      const values = opened.file.cas[mod];
      if (values === undefined) continue;
      ecrire(mod, values);
      // Every restored module is recalculated, so mark it shown: revealing its tab later must not
      // spend a second request on a result already on screen.
      tabController.markShown(mod);
      void calculer(mod);
    }

    tabController.onglet(tabToRestore(opened.file));
  });
});

/* ================================================================================================
 * 2. P2 chrome
 * ============================================================================================== */

function setNote(text: string): void {
  const note = chrome.querySelector<HTMLElement>('#p2-note');
  if (note !== null) note.textContent = text;
}

function renderChrome(): void {
  if (me === undefined) {
    chrome.hidden = true;
    return;
  }

  chrome.hidden = false;
  chrome.innerHTML = `${saveControlHTML(currentProject?.name)}<span class="p2-note" id="p2-note"></span>${identityHTML(me)}`;

  chrome.querySelector<HTMLButtonElement>('#p2-browse')?.addEventListener('click', () => {
    routes.navigate({ name: 'projets' });
  });

  chrome.querySelector<HTMLButtonElement>('#p2-save-server')?.addEventListener('click', () => {
    if (currentProject === undefined) return;
    void cases.save(currentProject.id, tabController.courant).then((saved) => {
      // A saved case is now a stored case: showing its strip immediately is what tells the user the
      // thing on screen is the thing in the database.
      if (saved !== undefined) showStoredCase(saved);
    });
  });

  chrome.querySelector<HTMLButtonElement>('#p2-signout')?.addEventListener('click', () => {
    void store.signOut().then(() => {
      me = undefined;
      currentProject = undefined;
      closeStoredCase();
      renderChrome();
      requireSignIn();
    });
  });
}

/* ================================================================================================
 * Stored cases (US-214) and the recalculation diff (US-215)
 * ============================================================================================== */

/** Paint a stored snapshot, and put it in `DERNIER` so the note prints from it. */
function showStoredCase(stored: CaseResponse): void {
  openedCase = stored;
  DERNIER.set(stored.module, {
    input: stored.inputs as Record<string, string | number>,
    envelope: stored.results,
  });
  must(`#res_${stored.module}`).innerHTML = panelHTML(
    stored.module,
    stored.inputs as Record<string, string | number>,
    stored.results,
  );

  openBar.innerHTML = caseHeaderHTML(stored);
  banner.innerHTML =
    running === undefined
      ? ''
      : versionBannerHTML(
          { engineVersion: stored.engineVersion, catalogVersion: stored.catalogVersion },
          running,
        );

  openBar.querySelector<HTMLButtonElement>('#p2-recalc')?.addEventListener('click', () => {
    void recalc.show(stored.id);
  });
  openBar.querySelector<HTMLButtonElement>('#p2-close-case')?.addEventListener('click', () => {
    closeStoredCase();
    void calculer(stored.module);
  });
}

function closeStoredCase(): void {
  openedCase = undefined;
  openBar.innerHTML = '';
  banner.innerHTML = '';
  recalc.hide();
}

const cases = caseView({
  fetchCase: (caseId) => store.fetchCase(caseId).then(toPlain),
  saveCase: (projectId, body) => store.saveCase(projectId, body).then(toPlain),
  writeForm: (module, inputs) => ecrire(module, inputs),
  readForm: (module) => lire(module),
  showStored: showStoredCase,
  showModule: (module) => {
    tabController.onglet(module);
    // The snapshot is about to be painted, so a later reveal of this tab must not spend a request
    // re-deriving a result that is already on screen.
    tabController.markShown(module);
  },
  setMessage: setNote,
});

/**
 * `ApiResult` → the shape the controllers ask for.
 *
 * The controllers take `{ ok, value?, kind? }` rather than importing `ApiResult`, so that their
 * tests can hand them a plain object. This is the one place the two shapes meet.
 */
function toPlain<T>(result: { ok: true; value: T } | { ok: false; kind: string }): {
  ok: boolean;
  value?: T;
  kind?: string;
} {
  return result.ok ? { ok: true, value: result.value } : { ok: false, kind: result.kind };
}

const recalc = recalcView(diffHost, {
  preview: (caseId) => store.recalcPreview(caseId).then(toPlain),
  confirm: (caseId) => store.recalcConfirm(caseId).then(toPlain),
  onConfirmed: async (caseId) => {
    // Re-read rather than patch the copy in memory: the case has a new snapshot, new versions and a
    // new integrity hash, and the authoritative version of all three is the row.
    const reloaded = await store.fetchCase(caseId);
    if (reloaded.ok) showStoredCase(reloaded.value);
  },
  onCancel: () => {
    setNote('');
  },
});

/* ================================================================================================
 * The project browser (US-213)
 * ============================================================================================== */

const projects = projectsView(overlay, {
  listProjects: () => store.listProjects().then(toPlain),
  listCases: (projectId) => store.listCases(projectId).then(toPlain),
  createProject: (name) =>
    store.createProject(name).then((result) => {
      if (result.ok) {
        currentProject = { id: result.value.id, name: result.value.name };
        renderChrome();
      }
      return { ok: result.ok };
    }),
  deleteCase: (caseId) =>
    store.deleteCase(caseId).then((result) => {
      // A deleted case must not stay on screen behind the browser. It is still fetchable by id for
      // an audit, but the user just said they were done with it.
      if (result.ok && openedCase?.id === caseId) closeStoredCase();
      return { ok: result.ok };
    }),
  openCase: (caseId) => {
    routes.navigate({ name: 'cas', caseId });
  },
  selectProject: (projectId) => {
    // The name comes from the row the browser already has; re-fetching it would be a request for
    // something on screen.
    const button = overlay.querySelector<HTMLButtonElement>(`[data-project="${projectId ?? ''}"]`);
    currentProject =
      projectId === undefined
        ? undefined
        : { id: projectId, name: button?.textContent ?? projectId };
    renderChrome();
  },
  confirm: (message) => window.confirm(message),
});

/* ================================================================================================
 * 3. Identity, routing, and the first calculation
 * ============================================================================================== */

/**
 * A successful sign-in has to **learn who signed in** before anything else happens.
 *
 * The first version of this called `start()` directly, and the effect in a browser was that
 * signing in cleared the form and showed the sign-in screen again: `me` was still undefined, so the
 * router immediately decided there was no session and sent the user back. No unit test could see
 * it, and the Playwright suite could not either — its fixture signs in through `fetch` and reloads,
 * which takes the boot path rather than this one. It took opening the page.
 *
 * The lesson is in the spec now: `accounts.spec.ts` signs in through the FORM as well.
 */
const auth = authView(
  overlay,
  { signIn: store.signIn, acceptInvite: store.acceptInvite },
  async () => {
    const identity = await store.fetchMe();
    if (!identity.ok) {
      auth.setMessage(failureMessage(identity.kind));
      return;
    }
    me = identity.value;
    await start(routes.takeDestination());
  },
);

/** Show the sign-in view, remembering where the user was trying to be. */
function requireSignIn(message?: string): void {
  me = undefined;
  renderChrome();
  routes.rememberDestination();
  setOverlay(true);
  auth.showSignIn(message);
}

const routes = router({
  onRoute: (route) => {
    void show(route);
  },
});

/** Render a route. Only ever called once a session exists, except for the invite view. */
async function show(route: Route): Promise<void> {
  if (route.name === 'invitation') {
    setOverlay(true);
    auth.showInvite(route.token);
    return;
  }

  if (me === undefined) {
    requireSignIn();
    return;
  }

  if (route.name === 'projets') {
    setOverlay(true);
    await projects.render(currentProject?.id);
    return;
  }

  setOverlay(false);

  if (route.name === 'cas') {
    await cases.open(route.caseId);
    return;
  }

  // The calculator. If a stored case is open we leave it; otherwise make sure something is on
  // screen for the active module.
  if (openedCase === undefined && !DERNIER.has(tabController.courant)) {
    await calculer(tabController.courant);
  }
}

/** Everything that happens once we know who the user is. */
async function start(route: Route): Promise<void> {
  renderChrome();

  // The running versions, for the banner. A failure here is not fatal: the banner simply does not
  // appear, which is the safe direction — better no banner than one comparing against a guess.
  const version = await store.fetchVersion();
  if (version.ok) {
    running = {
      engineVersion: version.value.engineVersion,
      catalogVersion: version.value.catalogVersion,
    };
  }

  routes.navigate(route);
}

/**
 * Boot.
 *
 * The invite route is checked BEFORE `/v1/me`, because its whole point is that the caller has no
 * account yet: asking who they are first would answer 401 and bounce them to a sign-in form for an
 * account that does not exist.
 */
async function boot(): Promise<void> {
  const route = routes.current();

  if (route.name === 'invitation') {
    setOverlay(true);
    auth.showInvite(route.token);
    return;
  }

  const identity = await store.fetchMe();

  if (!identity.ok) {
    requireSignIn();
    return;
  }

  me = identity.value;
  await start(route);
}

void boot();
